import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import i18n from "i18next";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "content_view_logged";

type Tracked = { key: string; section: string };

/** Maps a pathname to a stable page key + section, or null when not tracked. */
function resolvePage(pathname: string): Tracked | null {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (path === "/blog") return { key: "blog", section: "blog" };
  if (path.startsWith("/blog/")) {
    return { key: `blog:${path.slice("/blog/".length)}`, section: "blog" };
  }

  const info: Record<string, string> = {
    "/about": "about",
    "/how-it-works": "howItWorks",
    "/scoring-rules": "scoringRules",
    "/special-bets": "specialBets",
    "/help": "help",
    "/contact": "contact",
    "/credits": "credits",
  };
  if (info[path]) return { key: info[path], section: "info" };

  const legal: Record<string, string> = {
    "/terms": "terms",
    "/privacy": "privacy",
    "/cookies": "cookies",
  };
  if (legal[path]) return { key: legal[path], section: "legal" };

  return null;
}

/**
 * Logs a view of public content pages (blog, info pages, legal pages).
 * One record per page per browser session.
 */
export const ContentViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const page = resolvePage(location.pathname);
    if (!page) return;

    try {
      const logged = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "[]") as string[];
      if (logged.includes(page.key)) return;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...logged, page.key]));
    } catch {
      /* ignore storage errors */
    }

    const params = new URLSearchParams(location.search);
    void (async () => {
      const { data } = await supabase.auth.getSession();
      await supabase.from("content_page_views").insert({
        page_key: page.key,
        path: location.pathname,
        section: page.section,
        lang: i18n.language?.slice(0, 2) ?? null,
        referrer: document.referrer ? document.referrer.slice(0, 500) : null,
        campaign: params.get("utm_campaign"),
        source: params.get("utm_source"),
        user_id: data.session?.user.id ?? null,
      });
    })();
  }, [location.pathname, location.search]);

  return null;
};
